import { useState, useEffect } from "react";
import { auth, db } from "../firebase/firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./LoginComponent.scss";

const IS_MAINTENANCE_MODE = false;

const LoginComponent = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  const redirectToDashboard = async (uid) => {
    try {
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        setErrorMessage("User profile not found.");
        return;
      }

      const userData = userSnap.data();
      const slug = userData?.slug;

      if (!slug) {
        setErrorMessage("Slug not found in user profile.");
        return;
      }

      navigate(`/sites/${slug}/dashboard`);
    } catch (error) {
      console.error("Redirect error:", error);
      setErrorMessage("Failed to load your dashboard.");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      await redirectToDashboard(userCredential.user.uid);
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("Login failed. Please check your email and password.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!IS_MAINTENANCE_MODE && user?.uid) {
      redirectToDashboard(user.uid);
    }
  }, [user]);

  if (IS_MAINTENANCE_MODE) {
    return (
      <div className="login login--maintenance">
        <div className="login__overlay">
          <div className="login__card login__maintenanceCard">
            <div className="login__maintenanceIcon">⚙️</div>

            <p className="login__eyebrow">Temporary Maintenance</p>

            <h1 className="login__brand">We&rsquo;ll be back soon</h1>

            <p className="login__maintenanceText">
              Our site is currently undergoing scheduled maintenance to improve
              your experience.
            </p>

            <p className="login__maintenanceText">
              We sincerely apologize for the inconvenience. Please check back
              shortly.
            </p>

            <div className="login__maintenanceNote">
              Thank you for your patience.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login__overlay">
        <div className="login__card">
          <div className="login__header">
            <p className="login__eyebrow">Welcome back</p>
            <h1 className="login__brand">Guest Management System</h1>
            <p className="login__subtitle">Login to continue</p>
          </div>

          <form className="login__form" onSubmit={handleLogin}>
            <div className="login__field">
              <label className="login__label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="login__input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                inputMode="email"
              />
            </div>

            <div className="login__field">
              <label className="login__label" htmlFor="password">
                Password
              </label>

              <div className="login__passwordWrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="login__input login__input--password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="login__togglePassword"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {errorMessage && <p className="login__error">{errorMessage}</p>}

            <ButtonComponent
              as="button"
              type="submit"
              className="login__submit ui-btn"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </ButtonComponent>
          </form>

          <div className="login__footer">
            <p className="login__signupText">
              Not registered yet?{" "}
              <Link to="/signup" className="login__signupLink">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;